import React, { useState, useEffect, useRef } from 'react';
import SearchBar from '../components/search/SearchBar';
import FilterPanel from '../components/search/FilterPanel';
import UniversityCard from '../components/university/UniversityCard';
import UniversityRecommendations from '../components/recommendations/UniversityRecommendations';
import FeaturedUniversitiesSection from '../components/university/FeaturedUniversitiesSection';
import ProfileCompletionProgress from '../components/ProfileCompletionProgress';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon, FunnelIcon, ArrowsUpDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import useDocumentMetadata from '../hooks/useDocumentMetadata';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import { cachedFetch } from '../utils/apiCache';

const feeRanges = [
  { label: 'Under PKR 50,000', value: '0-50000' },
  { label: 'PKR 50,000 - 100,000', value: '50000-100000' },
  { label: 'PKR 100,000 - 200,000', value: '100000-200000' },
  { label: 'Above PKR 200,000', value: '200000+' }
];

const Home = () => {
  useDocumentMetadata(
    'Dakhla - University Admissions in Pakistan',
    'Discover, compare, and apply to top Pakistani universities. Track admission deadlines, compare fee structures, and apply with ease on Dakhla.'
  );

  const { isAuthenticated, isStudent } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [filters, setFilters] = useState({
    city: '',
    discipline: '',
    feeRange: '',
    deadline: ''
  });
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUniversities, setTotalUniversities] = useState(0);
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return false;
  });
  const listRef = useRef(null);
  const isInitialMount = useRef(true);
  const requestIdRef = useRef(0);

  // Handle mobile/desktop transition to prevent filter drawer from popping open on resize/zoom
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let wasDesktop = window.innerWidth >= 1280;

    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1280;
      if (wasDesktop && !isDesktop) {
        // Transitioned from desktop to mobile: hide filters so mobile drawer doesn't pop up
        setShowFilters(false);
      } else if (!wasDesktop && isDesktop) {
        // Transitioned from mobile to desktop: show filters inline by default
        setShowFilters(true);
      }
      wasDesktop = isDesktop;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search term changes (typing in search bar)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load static universities from backend with deduplication and caching
  const loadStaticUniversities = async (pageNum = 1, currentFilters = filters, currentSearch = debouncedSearchTerm, currentSortBy = sortBy) => {
    // Stamp this request so stale completions can be discarded
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const baseUrl = apiUrl.replace('/api', ''); // Remove /api suffix for image URLs

      // Construct query params
      const perPage = 10;
      // If feeRange is set we need to search across the whole dataset so fetch a large limit
      const fetchLimit = currentFilters.feeRange ? 10000 : perPage;
      const params = new URLSearchParams({
        // When fetching the full dataset for client-side fee filtering, always request page 1
        page: currentFilters.feeRange ? 1 : pageNum,
        limit: fetchLimit,
        search: currentSearch,
        city: currentFilters.city,
        discipline: currentFilters.discipline,
        sort_by: currentSortBy
      });

      const result = await cachedFetch(`${apiUrl}/static-universities?${params.toString()}`, {}, 60000);

      // Discard result if a newer request has since been issued
      if (requestId !== requestIdRef.current) return;

      if (result && result.ok && result.data) {
        const data = result.data;

        let newUniversitiesData = [];
        let hasNextPage = false;
        let totalPagesCount = 0;
        let serverTotalCount = 0;

        if (data.universities && Array.isArray(data.universities)) {
          newUniversitiesData = data.universities;
          hasNextPage = data.has_next;
          totalPagesCount = data.total_pages || 1;
          serverTotalCount = data.total_count || newUniversitiesData.length;
        } else {
          newUniversitiesData = Object.values(data);
          hasNextPage = false;
          totalPagesCount = 1;
          serverTotalCount = newUniversitiesData.length;
        }

        const formattedUniversitiesRaw = newUniversitiesData.map(uni => {
          const uniLogo = uni.profile_picture || uni.logo;
          const logoUrl = uniLogo ? (uniLogo.startsWith('/static/') ? `${baseUrl}${uniLogo}` : uniLogo) : '';
          return {
            ...uni,
            id: uni.id,
            name: uni.name || uni.university_name,
            location: uni.location || uni.address,
            established: uni.established || uni.established_year,
            about: uni.about || uni.about_university,
            logo: logoUrl,
            programs: uni.programs || [],
            tuitionFeeRaw: uni.programs?.[0]?.fee || uni.tuition_fee || null,
            deadline: uni.deadlines?.[0]?.deadline || uni.admission_deadline || 'N/A',
            city: (uni.location || uni.address) ? (uni.location || uni.address).split(',')[0].trim() : '',
            discipline: uni.programs?.[0]?.name || 'Various',
            type: uni.type,
            is_partner: uni.is_partner === true,
            isStatic: uni.is_partner !== true,
            slug: uni.slug
          };
        });

        // Helper to parse fee string like "190,000 PKR" -> number 190000
        const parseFeeNumber = (feeStr) => {
          if (!feeStr) return null;
          try {
            const matched = String(feeStr).match(/[0-9.,]+/);
            if (!matched) return null;
            const num = matched[0].replace(/[,]/g, '');
            return parseFloat(num);
          } catch (e) {
            return null;
          }
        };

        const formattedUniversities = formattedUniversitiesRaw.map(uni => ({
          ...uni,
          tuitionFee: uni.tuitionFeeRaw ? uni.tuitionFeeRaw : 'Contact',
          tuitionFeeNumber: parseFeeNumber(uni.tuitionFeeRaw)
        }));

        // Apply feeRange filter client-side if provided (we may have fetched all results)
        let filteredUniversities = formattedUniversities;
        if (currentFilters.feeRange) {
          const fr = currentFilters.feeRange; // e.g. '50000-100000' or '200000+'
          if (fr.endsWith('+')) {
            const min = parseFloat(fr.replace('+', ''));
            filteredUniversities = filteredUniversities.filter(u => u.tuitionFeeNumber != null && u.tuitionFeeNumber >= min);
          } else {
            const parts = fr.split('-');
            if (parts.length === 2) {
              const min = parseFloat(parts[0]);
              const max = parseFloat(parts[1]);
              filteredUniversities = filteredUniversities.filter(u => u.tuitionFeeNumber != null && u.tuitionFeeNumber >= min && u.tuitionFeeNumber <= max);
            }
          }
        }

        // Apply client-side sorting
        let sortedUniversities = filteredUniversities;
        if (currentSortBy === 'name') {
          sortedUniversities = [...filteredUniversities].sort((a, b) => a.name.localeCompare(b.name));
        } else if (currentSortBy === 'location') {
          sortedUniversities = [...filteredUniversities].sort((a, b) => a.location.localeCompare(b.location));
        }

        // If we fetched all results (fetchLimit > perPage) then we must paginate client-side
        let displayUniversities = sortedUniversities;
        let totalResults = sortedUniversities.length;
        let totalPagesFinal = totalPagesCount;
        let hasNext = hasNextPage;

        if (fetchLimit > perPage) {
          totalPagesFinal = Math.max(0, Math.ceil(totalResults / perPage));
          const start = (pageNum - 1) * perPage;
          const end = start + perPage;
          displayUniversities = sortedUniversities.slice(start, end);
          hasNext = pageNum < totalPagesFinal;
        } else {
          totalResults = serverTotalCount;
        }

        setUniversities(displayUniversities.map(uni => ({
          ...uni,
          id: uni.id,
          name: uni.name,
          location: uni.location,
          established: uni.established || uni.established_year,
          about: uni.about || uni.about_university,
          logo: uni.logo,
          programs: (uni.programs || []).map(p => typeof p === 'string' ? p : p.name || p),
          tuitionFee: uni.tuitionFee,
          deadline: uni.deadline,
          city: uni.city,
          discipline: uni.discipline,
          type: uni.type,
          isStatic: uni.isStatic,
          is_partner: uni.is_partner,
          slug: uni.slug
        })));

        // When there are no results, set totalPages to 0 so pagination hides
        setTotalPages(totalPagesFinal);
        setHasMore(hasNext);
        setTotalUniversities(totalResults);
      }
    } catch (error) {
      console.error('Error loading static universities:', error);
    } finally {
      // Only update loading state if this is still the latest request
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  // Centralized query effect: executes exactly once per query change.
  // Uses requestIdRef for stale-result discarding instead of AbortController signals,
  // which would poison cachedFetch's shared in-flight promise pool in React 18 StrictMode.
  useEffect(() => {
    loadStaticUniversities(page, filters, debouncedSearchTerm, sortBy);
  }, [page, filters, debouncedSearchTerm, sortBy]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // Scroll to the top of the list instead of the whole page
      listRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSearch = (newSearch, isSubmit) => {
    setSearchTerm(newSearch);
    if (isSubmit) {
      setPage(1);
      setDebouncedSearchTerm(newSearch);
      listRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(value => value !== '').length;

 return (
 <div className="min-h-screen bg-slate-100">
 {/* Hero Section */}
 <div className="relative bg-[#1E256F] text-white py-24 overflow-hidden shadow-sm">
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
  <div className="max-w-4xl mx-auto">
  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-3 whitespace-nowrap sm:whitespace-normal">
  Admissions Made Simple
  </h1>
  <p className="text-sm md:text-base lg:text-lg text-white/90 mb-6 font-normal max-w-3xl mx-auto md:whitespace-nowrap">
  Explore programs, fee structures, deadlines, and apply directly — all in one place.
  </p>
  <div className="bg-white rounded-xl p-1.5 shadow-lg max-w-2xl mx-auto">
  <SearchBar onSearch={handleSearch} />
  </div>
  </div>
  </div>
 </div>

 {/* Main Content */}
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
 {/* Profile Completion Progress - Only for authenticated students */}
 {isAuthenticated && isStudent && (
 <div className="mb-6">
 <ProfileCompletionProgress />
 </div>
 )}

 {/* Recommendations Section */}
 <UniversityRecommendations />

  {/* Featured Universities Section - Full Width Row */}
  {/* <FeaturedUniversitiesSection /> */}

 <div className="flex flex-col xl:flex-row gap-6 items-start">
 {/* Filters Sidebar */}
 <div
 className={`w-full xl:w-1/4 xl:flex-shrink-0 xl:sticky xl:z-10 transition-all duration-300 ${
 showFilters ? 'hidden xl:block opacity-100' : 'hidden xl:hidden opacity-0'
 }`}
 style={{ top: 'calc(var(--navbar-height, 64px) + 24px)' }}
 >
 <FilterPanel filters={filters} onFilterChange={handleFilterChange} feeRanges={feeRanges} />
 </div>

 {/* Universities List Column */}
 <div
 className="w-full xl:w-auto xl:flex-1 xl:min-w-0 transition-all duration-300"
 ref={listRef}
 style={{ scrollMarginTop: 'calc(var(--navbar-height, 64px) + 24px)' }}
 >
 {/* Controls Toolbar */}
 <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
 <div className="flex items-center gap-3">
 <h2 className="text-lg font-bold text-slate-800">
 {totalUniversities} {totalUniversities === 1 ? 'University' : 'Universities'} Found
 </h2>
 </div>

 <div className="flex flex-wrap items-center gap-3">
 {/* Toggle Filter Button */}
 <button
 onClick={() => setShowFilters(!showFilters)}
 className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold transition-all duration-300 ${
 showFilters
 ? 'bg-primary-50 border-primary-200 text-primary-700 hover:bg-primary-100'
 : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
 }`}
 >
 <FunnelIcon className="h-4 w-4" />
 <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
 </button>

 {/* Sort Dropdown */}
 <div className="relative flex items-center shadow-sm rounded-xl">
 <ArrowsUpDownIcon className="h-4 w-4 absolute left-3 text-slate-500 pointer-events-none" />
 <select
 value={sortBy}
 onChange={(e) => handleSortChange(e.target.value)}
 aria-label="Sort universities by"
 className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-xl pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-all cursor-pointer appearance-none"
 >
 <option value="name">Name (A-Z)</option>
 <option value="location">Location (A-Z)</option>
 </select>
 <ChevronDownIcon className="h-4 w-4 absolute right-3 text-slate-500 pointer-events-none" />
 </div>
 </div>
 </div>

 {/* Active Filters Pills */}
 {activeFilterCount > 0 && (
 <div className="flex flex-wrap gap-2 items-center mb-4 animate-fade-in">
 {filters.city && (
 <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
 {filters.city}
 <button 
 onClick={() => handleFilterChange({ ...filters, city: '' })} 
 className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
 aria-label="Remove city filter"
 >
 ✕
 </button>
 </span>
 )}
 {filters.discipline && (
 <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
 {filters.discipline}
 <button 
 onClick={() => handleFilterChange({ ...filters, discipline: '' })} 
 className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
 aria-label="Remove discipline filter"
 >
 ✕
 </button>
 </span>
 )}
 {filters.feeRange && (
 <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
 {feeRanges.find(r => r.value === filters.feeRange)?.label || filters.feeRange}
 <button 
 onClick={() => handleFilterChange({ ...filters, feeRange: '' })} 
 className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
 aria-label="Remove fee filter"
 >
 ✕
 </button>
 </span>
 )}
 {filters.deadline && (
 <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
 {filters.deadline === '30' ? 'Next 30 days' : filters.deadline === '60' ? 'Next 60 days' : 'Next 90 days'}
 <button 
 onClick={() => handleFilterChange({ ...filters, deadline: '' })} 
 className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
 aria-label="Remove deadline filter"
 >
 ✕
 </button>
 </span>
 )}
 <button
 onClick={() => handleFilterChange({ city: '', discipline: '', feeRange: '', deadline: '' })}
 className="text-xs font-bold text-red-500 hover:text-red-700 transition-colors ml-2 px-2.5 py-1.5 hover:bg-red-50 rounded-lg"
 >
 Clear All
 </button>
 </div>
 )}

  {loading ? (
    <div className="py-12 flex justify-center">
      <DakhlaButterflyLoader text="Discovering Universities..." />
    </div>
  ) : (
 <>
 {/* Search Results List */}
 <div className="flex flex-col gap-4">
 {universities.map((university) => (
 <UniversityCard key={university.id} university={university} />
 ))}
 </div>

 {/* Pagination Controls */}
 {totalPages > 1 && (
 <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
 {/* Showing text */}
 <div className="text-sm text-slate-500 font-medium">
 Showing <span className="font-semibold text-slate-900">{(page - 1) * 10 + 1}</span> -{" "}
 <span className="font-semibold text-slate-900">{Math.min(page * 10, totalUniversities)}</span> of{" "}
 <span className="font-semibold text-slate-900">{totalUniversities}</span> results
 </div>

 {/* Page buttons */}
 <div className="flex items-center space-x-1.5">
 <button
 onClick={() => handlePageChange(page - 1)}
 disabled={page === 1}
 className="p-2 border border-slate-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
 aria-label="Previous page"
 >
 <ChevronLeftIcon className="h-4 w-4" />
 </button>

 {/* Page Numbers */}
 {[...Array(totalPages)].map((_, index) => {
 const pageNum = index + 1;
 if (
 pageNum === 1 ||
 pageNum === totalPages ||
 (pageNum >= page - 1 && pageNum <= page + 1)
 ) {
 return (
 <button
 key={pageNum}
 onClick={() => handlePageChange(pageNum)}
 className={`px-3 py-1.5 border rounded-lg text-sm font-semibold transition-all ${page === pageNum
 ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
 : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 shadow-sm'
 }`}
 >
 {pageNum}
 </button>
 );
 } else if (
 (pageNum === page - 2 && page > 3) ||
 (pageNum === page + 2 && page < totalPages - 2)
 ) {
 return <span key={pageNum} className="px-2 text-slate-400">...</span>;
 }
 return null;
 })}

 <button
 onClick={() => handlePageChange(page + 1)}
 disabled={page === totalPages}
 className="p-2 border border-slate-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
 aria-label="Next page"
 >
 <ChevronRightIcon className="h-4 w-4" />
 </button>
 </div>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 </div>

 {/* Mobile Filters Drawer */}
 {showFilters && (
 <div className="fixed inset-0 z-50 xl:hidden">
 {/* Backdrop */}
 <div 
 className="fixed inset-0 bg-slate-900/50 transition-opacity duration-300"
 onClick={() => setShowFilters(false)}
 />
 
 {/* Drawer Content */}
 <div className="fixed inset-y-0 left-0 w-full max-w-xs sm:max-w-sm bg-white shadow-2xl p-6 flex flex-col h-full animate-slide-in-left overflow-hidden">
 <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
 <div className="flex items-center gap-2">
 <FunnelIcon className="h-5 w-5 text-slate-700" />
 <h3 className="text-lg font-bold text-slate-800">Filters</h3>
 </div>
 <button 
 onClick={() => setShowFilters(false)}
 className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
 aria-label="Close filters menu"
 >
 <span className="text-xl font-bold">✕</span>
 </button>
 </div>
 
 <div className="flex-1 overflow-y-auto scrollbar-hide pr-1">
 <FilterPanel filters={filters} onFilterChange={handleFilterChange} feeRanges={feeRanges} isMobileDrawer={true} />
 </div>
 
 {/* View Results Button at Bottom for Mobile */}
 <div className="border-t border-slate-100 pt-4 mt-6">
 <button
 onClick={() => setShowFilters(false)}
 className="w-full btn-primary text-sm flex justify-center items-center py-3"
 >
 Show {totalUniversities} {totalUniversities === 1 ? 'Result' : 'Results'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default Home;
